/** Serverzustand der Sicherungsuebersicht -- F5-01. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import {
  fetchBackupDevice,
  fetchBackupDevices,
  fetchBackupDevicesForTicket,
  fetchBackupJob,
  fetchBackupJobs,
  fetchBackupSummary,
  restartBackup,
  syncBackupDevices,
  verifyBackupState,
  type DeviceFilters,
} from './api';

export const backupKeys = {
  all: ['backup'] as const,
  summary: ['backup', 'summary'] as const,
  devices: (filters: DeviceFilters) => ['backup', 'devices', filters] as const,
  ticketDevices: (ticketId: string) => ['backup', 'ticket-devices', ticketId] as const,
  device: (deviceId: string) => ['backup', 'device', deviceId] as const,
  jobs: (deviceId?: string) => ['backup', 'jobs', deviceId ?? null] as const,
  job: (jobId: string) => ['backup', 'job', jobId] as const,
  verification: ['backup', 'verification'] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

export function useBackupSummary() {
  return useQuery({
    queryKey: backupKeys.summary,
    queryFn: fetchBackupSummary,
    retry: retryUnlessPermanent,
  });
}

export function useBackupDevices(filters: DeviceFilters = {}) {
  return useQuery({
    queryKey: backupKeys.devices(filters),
    queryFn: () => fetchBackupDevices(filters),
    retry: retryUnlessPermanent,
  });
}

/**
 * Die Geraete zu einem Ticket.
 *
 * ``enabled`` steuert der Aufrufer nicht: Die Frage "ist das ein
 * Backup-Ticket" laesst sich nur beantworten, indem man sie stellt. Die
 * Antwort ist im Regelfall eine leere Liste, und die kostet eine Abfrage --
 * weniger als eine Spalte am Ticket, die bei jedem Vorgang mitgefuehrt
 * werden muesste.
 */
export function useBackupDevicesForTicket(ticketId: string) {
  return useQuery({
    queryKey: backupKeys.ticketDevices(ticketId),
    queryFn: () => fetchBackupDevicesForTicket(ticketId),
    retry: retryUnlessPermanent,
  });
}

export function useBackupDevice(deviceId: string | null) {
  return useQuery({
    queryKey: backupKeys.device(deviceId ?? ''),
    queryFn: () => fetchBackupDevice(deviceId ?? ''),
    enabled: deviceId !== null,
    retry: retryUnlessPermanent,
  });
}

export function useBackupVerification(enabled = false) {
  return useQuery({
    queryKey: backupKeys.verification,
    queryFn: verifyBackupState,
    enabled,
    retry: retryUnlessPermanent,
  });
}

export function useBackupJobs(deviceId?: string) {
  return useQuery({
    queryKey: backupKeys.jobs(deviceId),
    queryFn: () => fetchBackupJobs(deviceId),
    retry: retryUnlessPermanent,
  });
}

/**
 * Der Stand eines laufenden Auftrags.
 *
 * **Nachgefragt wird nur, solange er laeuft.** Das ist das "sinnvolle
 * Intervall" aus der Anforderung: Der Server fragt beim Anbieter nach,
 * solange der Auftrag offen ist, und antwortet sonst aus dem Bestand. Die
 * Oberflaeche hoert von selbst auf zu fragen, sobald ein Endzustand
 * dasteht -- ein Zeitgeber, der nach dem Abschluss weiterlaeuft, ist ein
 * Aufruf je zehn Sekunden fuer eine Antwort, die sich nicht mehr aendert.
 */
export function useBackupJob(jobId: string | null) {
  return useQuery({
    queryKey: backupKeys.job(jobId ?? ''),
    queryFn: () => fetchBackupJob(jobId ?? ''),
    enabled: jobId !== null,
    retry: retryUnlessPermanent,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      const laeuft = status === 'created' || status === 'approved' || status === 'in_progress';
      return laeuft ? 10_000 : false;
    },
  });
}

function useBackupMutation<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      // Der ganze Zweig: Ein Abgleich aendert Liste, Zahlen und Abgleich
      // zugleich, und welche Zeile er beruehrt hat, weiss nur der Server.
      await queryClient.invalidateQueries({ queryKey: backupKeys.all });
    },
  });
}

export function useSyncBackup() {
  return useBackupMutation(() => syncBackupDevices());
}

export function useRestartBackup() {
  return useBackupMutation((variables: { deviceId: string; ticketId?: string }) =>
    restartBackup(variables.deviceId, variables.ticketId),
  );
}
